import React, { useState, useMemo } from "react";
import { CheckCircle2, Eye, Gauge } from "lucide-react";
import DetailSlidePanel from "./DetailSlidePanel";

/**
 * InteractiveColumns component - displays Failure Modes, Tests, and Monitoring columns
 * with Connected Highlighting and Details Panel features
 * 
 * Click behavior:
 * - Single click on item = Highlight related items only
 * - Click "View Details" button = Open horizontal slide panel
 */
const InteractiveColumns = ({
  component, // The selected component with enhanced data
  onSelectItem, // Callback when an item is selected (type, itemName, componentId)
  isItemSelected, // Function to check if item is selected (type, itemName, componentId)
  compact = false // Compact mode for presentation view
}) => {
  const [detailItem, setDetailItem] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [highlightedItems, setHighlightedItems] = useState({ tests: [], monitoring: [], failureModes: [] });

  // Get the full item data (not just the name)
  const getFullItemData = (type, itemName) => {
    if (!component) return null;
    
    if (type === "failure_mode") {
      return component.failureModes?.find(f => f.name === itemName);
    } else if (type === "test") {
      return component.tests?.find(t => t.name === itemName);
    } else if (type === "monitoring") {
      return component.monitoring?.find(m => m.name === itemName);
    }
    return null;
  };

  // Handle clicking on an item - ONLY toggle highlight (no modal)
  const handleItemClick = (type, itemName, e) => {
    e.stopPropagation();
    
    const itemData = getFullItemData(type, itemName);
    
    // Toggle highlighting for related items
    if (type === "failure_mode" && itemData) {
      const isCurrentlyHighlighted = highlightedItems.failureModes.includes(itemName);
      
      if (isCurrentlyHighlighted) {
        setHighlightedItems({ tests: [], monitoring: [], failureModes: [] });
      } else {
        setHighlightedItems({
          failureModes: [itemName],
          tests: itemData.relatedTests || [],
          monitoring: itemData.relatedMonitoring || []
        });
      }
    } else if (type === "test" && itemData) {
      const isCurrentlyHighlighted = highlightedItems.tests.includes(itemName);
      
      if (isCurrentlyHighlighted) {
        setHighlightedItems({ tests: [], monitoring: [], failureModes: [] });
      } else {
        setHighlightedItems({
          failureModes: itemData.detectsFailures || [],
          tests: [itemName],
          monitoring: []
        });
      }
    } else if (type === "monitoring" && itemData) {
      const isCurrentlyHighlighted = highlightedItems.monitoring.includes(itemName);
      
      if (isCurrentlyHighlighted) {
        setHighlightedItems({ tests: [], monitoring: [], failureModes: [] });
      } else {
        setHighlightedItems({
          failureModes: itemData.detectsFailures || [],
          tests: [],
          monitoring: [itemName]
        });
      }
    }
  };

  // Handle View Details button click - opens the slide panel
  const handleViewDetails = (type, itemName, e) => {
    e.stopPropagation();
    
    const itemData = getFullItemData(type, itemName);
    if (itemData) {
      setDetailItem(itemData);
      setDetailType(type);
      
      // Also set highlights when viewing details
      if (type === "failure_mode") {
        setHighlightedItems({
          failureModes: [itemName],
          tests: itemData.relatedTests || [],
          monitoring: itemData.relatedMonitoring || []
        });
      } else if (type === "test") {
        setHighlightedItems({
          failureModes: itemData.detectsFailures || [],
          tests: [itemName],
          monitoring: []
        });
      } else if (type === "monitoring") {
        setHighlightedItems({
          failureModes: itemData.detectsFailures || [],
          tests: [],
          monitoring: [itemName]
        });
      }
    }
  };

  // Handle checkbox click (selection for summary)
  const handleCheckboxClick = (type, itemName, componentId, e) => {
    e.stopPropagation();
    onSelectItem(type, itemName, componentId);
  };

  // Get related items for the detail panel
  const getRelatedItems = () => {
    if (!detailItem || !detailType) return null;
    
    if (detailType === "failure_mode") {
      return {
        tests: detailItem.relatedTests || [],
        monitoring: detailItem.relatedMonitoring || []
      };
    } else if (detailType === "test" || detailType === "monitoring") {
      return {
        failureModes: detailItem.detectsFailures || []
      };
    }
    return null;
  };

  // Handle selecting a related item from the detail panel
  const handleSelectRelated = (type, itemName) => {
    const itemData = getFullItemData(type, itemName);
    if (itemData) {
      setDetailItem(itemData);
      setDetailType(type);
      
      // Update highlights
      if (type === "failure_mode") {
        setHighlightedItems({
          failureModes: [itemName],
          tests: itemData.relatedTests || [],
          monitoring: itemData.relatedMonitoring || []
        });
      } else if (type === "test") {
        setHighlightedItems({
          failureModes: itemData.detectsFailures || [],
          tests: [itemName],
          monitoring: []
        });
      } else if (type === "monitoring") {
        setHighlightedItems({
          failureModes: itemData.detectsFailures || [],
          tests: [],
          monitoring: [itemName]
        });
      }
    }
  };

  // Check if an item should be highlighted
  const isHighlighted = (type, itemName) => {
    if (type === "failure_mode") return highlightedItems.failureModes.includes(itemName);
    if (type === "test") return highlightedItems.tests.includes(itemName);
    if (type === "monitoring") return highlightedItems.monitoring.includes(itemName);
    return false;
  };

  // Get simple name arrays for backward compatibility
  const failureModeNames = useMemo(() => 
    component?.failureModes?.map(f => typeof f === 'string' ? f : f.name) || [],
    [component]
  );
  
  const testNames = useMemo(() => 
    component?.tests?.map(t => typeof t === 'string' ? t : t.name) || [],
    [component]
  );
  
  const monitoringNames = useMemo(() => 
    component?.monitoring?.map(m => typeof m === 'string' ? m : m.name) || [],
    [component]
  );

  if (!component) return null;

  const padding = compact ? "p-2.5" : "p-2";
  const spacing = compact ? "space-y-2" : "space-y-2";

  // Render individual item card with View Details button
  const renderItemCard = (type, itemName, idx, colorConfig) => {
    const isSelected = isItemSelected(type, itemName, component.id);
    const highlighted = isHighlighted(type, itemName);
    
    return (
      <div 
        key={idx}
        onClick={(e) => handleItemClick(type, itemName, e)}
        className={`${padding} rounded-md border text-sm cursor-pointer transition-all ${
          highlighted 
            ? `${colorConfig.highlightBg} ${colorConfig.highlightBorder} shadow-md ring-2 ${colorConfig.ring} ring-offset-1` 
            : isSelected 
              ? `${colorConfig.selectedBg} ${colorConfig.selectedBorder} shadow-sm` 
              : `bg-white ${colorConfig.defaultBorder} ${colorConfig.hoverBg}`
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="text-slate-700 flex-1 pr-2">{itemName}</span>
          <div className="flex items-center gap-1">
            {/* View Details Button */}
            <button
              onClick={(e) => handleViewDetails(type, itemName, e)}
              className={`p-1.5 rounded-md ${colorConfig.detailsBtnHover} transition-colors`}
              title="View Details"
            >
              <Eye className={`w-3.5 h-3.5 ${colorConfig.detailsIcon}`} />
            </button>
            {/* Select for Summary Checkbox */}
            <div 
              onClick={(e) => handleCheckboxClick(type, itemName, component.id, e)}
              className={`p-1 ${colorConfig.checkboxHover} rounded`}
            >
              <CheckCircle2 
                className={`w-4 h-4 flex-shrink-0 ${isSelected ? colorConfig.checkboxActive : 'text-slate-300 hover:text-slate-400'}`} 
                strokeWidth={1.5}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Color configurations for each column type
  const failureModeColors = {
    highlightBg: 'bg-red-200',
    highlightBorder: 'border-red-400',
    ring: 'ring-red-300',
    selectedBg: 'bg-red-100',
    selectedBorder: 'border-red-300',
    defaultBorder: 'border-red-200',
    hoverBg: 'hover:bg-red-50',
    detailsBtnHover: 'hover:bg-red-100',
    detailsIcon: 'text-red-400 hover:text-red-600',
    checkboxHover: 'hover:bg-red-100',
    checkboxActive: 'text-red-600'
  };

  const testColors = {
    highlightBg: 'bg-blue-200',
    highlightBorder: 'border-blue-400',
    ring: 'ring-blue-300',
    selectedBg: 'bg-blue-100',
    selectedBorder: 'border-blue-300',
    defaultBorder: 'border-blue-200',
    hoverBg: 'hover:bg-blue-50',
    detailsBtnHover: 'hover:bg-blue-100',
    detailsIcon: 'text-blue-400 hover:text-blue-600',
    checkboxHover: 'hover:bg-blue-100',
    checkboxActive: 'text-blue-600'
  };

  const monitoringColors = {
    highlightBg: 'bg-green-200',
    highlightBorder: 'border-green-400',
    ring: 'ring-green-300',
    selectedBg: 'bg-green-100',
    selectedBorder: 'border-green-300',
    defaultBorder: 'border-green-200',
    hoverBg: 'hover:bg-green-50',
    detailsBtnHover: 'hover:bg-green-100',
    detailsIcon: 'text-green-400 hover:text-green-600',
    checkboxHover: 'hover:bg-green-100',
    checkboxActive: 'text-green-600'
  };

  return (
    <>
      <div className={`grid grid-cols-3 gap-4`}>
        {/* Failure Modes Column */}
        <div className={`bg-red-50 rounded-lg ${compact ? 'p-4' : 'p-3'} border border-red-100`}>
          <h3 className={`font-semibold text-red-800 mb-3 flex items-center gap-2 ${compact ? 'text-sm' : 'text-xs'} uppercase tracking-wide`}>
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            Failure Modes
          </h3>
          <div className={spacing}>
            {failureModeNames.map((mode, idx) => 
              renderItemCard("failure_mode", mode, idx, failureModeColors)
            )}
          </div>
        </div>

        {/* Diagnostic Tests Column */}
        <div className={`bg-blue-50 rounded-lg ${compact ? 'p-4' : 'p-3'} border border-blue-100`}>
          <h3 className={`font-semibold text-blue-800 mb-3 flex items-center gap-2 ${compact ? 'text-sm' : 'text-xs'} uppercase tracking-wide`}>
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            Diagnostic Tests
          </h3>
          <div className={spacing}>
            {testNames.map((test, idx) => 
              renderItemCard("test", test, idx, testColors)
            )}
          </div>
        </div>

        {/* Continuous Monitoring Column */}
        <div className={`bg-green-50 rounded-lg ${compact ? 'p-4' : 'p-3'} border border-green-100`}>
          <h3 className={`font-semibold text-green-800 mb-3 flex items-center gap-2 ${compact ? 'text-sm' : 'text-xs'} uppercase tracking-wide`}>
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            Continuous Monitoring
          </h3>
          <div className={spacing}>
            {monitoringNames.map((monitor, idx) => 
              renderItemCard("monitoring", monitor, idx, monitoringColors)
            )}
          </div>
        </div>
      </div>

      {/* Horizontal Slide Detail Panel */}
      <DetailSlidePanel
        isOpen={!!detailItem}
        onClose={() => {
          setDetailItem(null);
          setDetailType(null);
        }}
        item={detailItem}
        itemType={detailType}
        componentName={component.name}
        componentColor={component.color}
        relatedItems={getRelatedItems()}
        onSelectRelated={handleSelectRelated}
      />
    </>
  );
};

export default InteractiveColumns;
